import {
  AccountBalance as AccountBalanceIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns-jalali";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWalletTransactions } from "../../../application/actions/financialActions";
import {
  selectTransactionsData,
  selectTransactionsLoading,
  selectTransactionsTotalCount,
} from "../../../application/selectors/financial";

const Transactions = () => {
  const dispatch = useDispatch();

  // Redux State
  const transactions = useSelector(selectTransactionsData);
  const loading = useSelector(selectTransactionsLoading);
  const totalCount = useSelector(selectTransactionsTotalCount);

  // Local State
  const [filters, setFilters] = useState({
    transactionType: "",
    fromDate: "",
    toDate: "",
    minAmount: "",
    maxAmount: "",
    description: "",
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 100,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);

  useEffect(() => {
    loadTransactions();
  }, [pagination.pageNumber, pagination.pageSize]);

  const loadTransactions = () => {
    const params = {
      ...filters,
      ...pagination,
    };
    dispatch(fetchWalletTransactions(params));
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handleApplyFilters = () => {
    setPagination({ ...pagination, pageNumber: 1 });
    loadTransactions();
  };

  const handleClearFilters = () => {
    setFilters({
      transactionType: "",
      fromDate: "",
      toDate: "",
      minAmount: "",
      maxAmount: "",
      description: "",
    });
    setPagination({ pageNumber: 1, pageSize: 100 });
  };

  const handlePageChange = (event, newPage) => {
    setPagination({
      ...pagination,
      pageNumber: newPage + 1,
    });
  };

  const handleRowsPerPageChange = (event) => {
    setPagination({
      pageNumber: 1,
      pageSize: parseInt(event.target.value, 10),
    });
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsDialog(true);
  };

  const handleExportToExcel = () => {
    // TODO: Implement Excel export
    console.log("Export to Excel");
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case "Deposit":
        return "واریز";
      case "Withdraw":
        return "برداشت";
      case "Payment":
        return "پرداخت";
      case "Refund":
        return "استرداد";
      case "Settlement":
        return "تسویه";
      case "Hold":
        return "بلوکه";
      case "Release":
        return "آزادسازی";
      default:
        return type;
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case "Deposit":
      case "Refund":
      case "Release":
        return "success";
      case "Withdraw":
      case "Payment":
      case "Settlement":
        return "error";
      case "Hold":
        return "warning";
      default:
        return "default";
    }
  };

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case "Deposit":
      case "Refund":
      case "Release":
        return <TrendingUpIcon />;
      case "Withdraw":
      case "Payment":
      case "Settlement":
        return <TrendingDownIcon />;
      default:
        return <AccountBalanceIcon />;
    }
  };

  const calculateSummary = () => {
    if (!transactions || transactions.length === 0) {
      return { totalDeposits: 0, totalWithdrawals: 0, balance: 0 };
    }

    const totalDeposits = transactions
      .filter((t) =>
        ["Deposit", "Refund", "Release"].includes(t.transactionType)
      )
      .reduce((sum, t) => sum + t.amount, 0);

    const totalWithdrawals = transactions
      .filter((t) =>
        ["Withdraw", "Payment", "Settlement"].includes(t.transactionType)
      )
      .reduce((sum, t) => sum + t.amount, 0);

    return {
      totalDeposits,
      totalWithdrawals,
      balance: totalDeposits - totalWithdrawals,
    };
  };

  const summary = calculateSummary();

  const transactionTypes = [
    "Deposit",
    "Withdraw",
    "Payment",
    "Refund",
    "Settlement",
    "Hold",
    "Release",
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            تراکنش‌های کیف پول
          </Typography>
          <Typography variant="body1" color="text.secondary">
            مشاهده کلیه تراکنش‌های مالی
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
        >
          خروجی Excel
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <CardContent>
              <Typography variant="h6" color="white" gutterBottom>
                کل واریزی‌ها
              </Typography>
              <Typography
                variant="h4"
                color="white"
                sx={{ fontWeight: "bold" }}
              >
                {summary.totalDeposits.toLocaleString("fa-IR")} ریال
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            }}
          >
            <CardContent>
              <Typography variant="h6" color="white" gutterBottom>
                کل برداشت‌ها
              </Typography>
              <Typography
                variant="h4"
                color="white"
                sx={{ fontWeight: "bold" }}
              >
                {summary.totalWithdrawals.toLocaleString("fa-IR")} ریال
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            }}
          >
            <CardContent>
              <Typography variant="h6" color="white" gutterBottom>
                خالص تغییرات
              </Typography>
              <Typography
                variant="h4"
                color="white"
                sx={{ fontWeight: "bold" }}
              >
                {summary.balance.toLocaleString("fa-IR")} ریال
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">فیلترها</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "پنهان کردن" : "نمایش فیلترها"}
          </Button>
        </Box>

        {showFilters && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>نوع تراکنش</InputLabel>
                <Select
                  value={filters.transactionType}
                  label="نوع تراکنش"
                  onChange={(e) =>
                    handleFilterChange("transactionType", e.target.value)
                  }
                >
                  <MenuItem value="">همه</MenuItem>
                  {transactionTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {getTransactionTypeLabel(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="حداقل مبلغ"
                value={filters.minAmount}
                onChange={(e) =>
                  handleFilterChange("minAmount", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="حداکثر مبلغ"
                value={filters.maxAmount}
                onChange={(e) =>
                  handleFilterChange("maxAmount", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="جستجو در توضیحات"
                value={filters.description}
                onChange={(e) =>
                  handleFilterChange("description", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                sx={{ height: "56px" }}
              >
                اعمال فیلترها
              </Button>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{ height: "56px" }}
              >
                پاک کردن فیلترها
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Transactions Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>تاریخ</TableCell>
                  <TableCell>نوع تراکنش</TableCell>
                  <TableCell align="right">مبلغ (ریال)</TableCell>
                  <TableCell>توضیحات</TableCell>
                  <TableCell>شماره پیگیری</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions && transactions.length > 0 ? (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>
                        {format(
                          new Date(transaction.createdAt),
                          "yyyy/MM/dd - HH:mm"
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getTransactionTypeIcon(
                            transaction.transactionType
                          )}
                          label={getTransactionTypeLabel(
                            transaction.transactionType
                          )}
                          color={getTransactionTypeColor(
                            transaction.transactionType
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {transaction.amount.toLocaleString("fa-IR")}
                      </TableCell>
                      <TableCell>{transaction.description || "-"}</TableCell>
                      <TableCell>
                        {transaction.referenceNumber || "-"}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          onClick={() => handleViewDetails(transaction)}
                        >
                          جزئیات
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        تراکنشی یافت نشد
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {transactions && transactions.length > 0 && (
            <TablePagination
              component="div"
              count={totalCount || 0}
              page={pagination.pageNumber - 1}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="تعداد در صفحه:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} از ${count}`
              }
            />
          )}
        </Paper>
      )}

      {/* Transaction Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>جزئیات تراکنش</DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    نوع تراکنش:
                  </Typography>
                  <Chip
                    icon={getTransactionTypeIcon(
                      selectedTransaction.transactionType
                    )}
                    label={getTransactionTypeLabel(
                      selectedTransaction.transactionType
                    )}
                    color={getTransactionTypeColor(
                      selectedTransaction.transactionType
                    )}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    مبلغ:
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {selectedTransaction.amount.toLocaleString("fa-IR")} ریال
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    تاریخ:
                  </Typography>
                  <Typography sx={{ mt: 1 }}>
                    {format(
                      new Date(selectedTransaction.createdAt),
                      "yyyy/MM/dd - HH:mm"
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    شماره پیگیری:
                  </Typography>
                  <Typography sx={{ mt: 1 }}>
                    {selectedTransaction.referenceNumber || "-"}
                  </Typography>
                </Grid>

                {selectedTransaction.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      توضیحات:
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                      {selectedTransaction.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>بستن</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Transactions;
